import { IMetaParams, IMetaTemplate } from './meta-wpp-template.service';
import {
    canonicalVariableName,
    defaultExampleForCustomVariable,
    startsSentenceAt,
    templateVariableDef,
} from './meta-template-variables';

/**
 * Leitura das variáveis que um template Meta exige de quem envia.
 *
 * Extraído de `MetaTemplatePicker.vue` porque o envio pelo chat (disparo rápido) precisa
 * das MESMAS regras e não usa o picker: ele mandava `{replace:null}` para qualquer
 * template, e a Meta recusava com 132000 ("number of localizable_params (0) does not
 * match the expected number of params"). Duas cópias destas regras significaria dois
 * lugares para divergir do que o servidor valida.
 */

export type SecaoParametro = 'header' | 'body';

export interface CampoParametro {
    section: SecaoParametro;
    /** Nome da variável (NOMEADO) ou posição (POSICIONAL). */
    key: string;
    label: string;
    /**
     * Valor de exemplo aprovado na Meta para esta variável, quando o template o
     * declara. É o que o campo mostra esmaecido quando está vazio — muito mais
     * útil do que um "Valor ou variável" genérico para saber o que se espera ali.
     */
    example?: string;
}

export function parseComponentJson(s?: string | null): Record<string, unknown> | null {
    try { return s ? (JSON.parse(s) as Record<string, unknown>) : null; } catch { return null; }
}

/** Nomes entre `{{ }}`, na ordem e sem repetir. */
export function extractPlaceholders(text?: unknown): string[] {
    if (typeof text !== 'string') return [];
    return [...new Set([...text.matchAll(/\{\{\s*([^}]+?)\s*\}\}/g)].map(m => m[1].trim()))];
}

/**
 * Chaves que o OPERADOR precisa preencher num componente.
 *
 * Variáveis do catálogo (`{{nome_contato}}`, `{{saudacao}}`) ficam de fora: o servidor as
 * resolve no envio, e pedir o valor aqui — ainda por cima exigindo-o — seria errado.
 * Vale também para o nome legado, que o servidor continua resolvendo.
 */
export function paramKeysOfComponent(type: string | null | undefined, json: Record<string, unknown> | null): string[] {
    const t = String(type ?? '');
    if (!t.includes('NAMED') && !t.includes('POSITIONAL')) return [];
    return extractPlaceholders(json?.text).filter(k => !canonicalVariableName(k));
}

export interface ParametrosDoTemplate {
    isNamed: boolean;
    headerKeys: string[];
    bodyKeys: string[];
    fields: CampoParametro[];
}

/** Tudo o que a UI precisa para montar (e validar) o formulário de um template. */
export function parametrosDoTemplate(template: IMetaTemplate | null | undefined): ParametrosDoTemplate {
    if (!template) return { isNamed: false, headerKeys: [], bodyKeys: [], fields: [] };

    const isNamed = template.parameterFormat === 'NAMED';
    const headerJson = parseComponentJson(template.headerJson);
    const bodyJson = parseComponentJson(template.bodyJson);
    const headerKeys = paramKeysOfComponent(template.headerType, headerJson);
    const bodyKeys = paramKeysOfComponent(template.bodyType, bodyJson);

    const exemplosHeader = exemplosDoComponente(headerJson);
    const exemplosBody = exemplosDoComponente(bodyJson);

    const fields: CampoParametro[] = [
        ...headerKeys.map(k => ({
            section: 'header' as const,
            key: k,
            label: isNamed ? `Cabeçalho · {{${k}}}` : `Cabeçalho · parâmetro ${k}`,
            example: exemplosHeader[k],
        })),
        ...bodyKeys.map(k => ({
            section: 'body' as const,
            key: k,
            label: isNamed ? `Corpo · {{${k}}}` : `Corpo · parâmetro ${k}`,
            example: exemplosBody[k],
        })),
    ];

    return { isNamed, headerKeys, bodyKeys, fields };
}

/** `true` quando enviar sem coletar valores resultaria no 132000 da Meta. */
export function templateExigeParametros(template: IMetaTemplate | null | undefined): boolean {
    return parametrosDoTemplate(template).fields.length > 0;
}

/** Monta o corpo de envio no formato que o template declara. */
export function buildMetaParams(
    p: ParametrosDoTemplate,
    values: { header: Record<string, string>; body: Record<string, string> },
): IMetaParams {
    const { isNamed, headerKeys: h, bodyKeys: b } = p;
    if (!h.length && !b.length) return { replace: null };

    if (isNamed) {
        return {
            header: h.map(k => [k, values.header[k] ?? ''] as [string, string]),
            body: b.map(k => [k, values.body[k] ?? ''] as [string, string]),
        };
    }

    // POSICIONAL: a Meta casa por ORDEM, então a ordenação numérica é o contrato.
    const ord = (arr: string[]) => [...arr].sort((a, c) => Number(a) - Number(c));
    return {
        header: ord(h).map(k => values.header[k] ?? ''),
        body: ord(b).map(k => values.body[k] ?? ''),
    };
}

// ---------------------------------------------------------------------------
// Substituição dos valores no texto
// ---------------------------------------------------------------------------

/** Mesmo padrão de `extractPlaceholders`, em forma global reutilizável. */
const PLACEHOLDER_GLOBAL = /\{\{\s*([^}]+?)\s*\}\}/g;

const capitalizarPrimeira = (valor: string): string =>
    valor ? valor.charAt(0).toUpperCase() + valor.slice(1) : valor;

/**
 * Valores de um componente, indexados pela chave que aparece no texto.
 *
 * NOMEADO já vem assim (`[nome, valor]`). POSICIONAL casa por ORDEM, então o
 * índice do array vira a chave `"1"`, `"2"`, … — que é exatamente o que
 * `{{1}}` escreve no texto.
 */
function valoresPorChave(secao: unknown): Record<string, string> {
    if (!Array.isArray(secao)) return {};

    const mapa: Record<string, string> = {};
    secao.forEach((item, indice) => {
        if (Array.isArray(item)) {
            const [chave, valor] = item as [string, string];
            if (typeof chave === 'string') mapa[chave.trim()] = String(valor ?? '');
            return;
        }
        if (typeof item === 'string') mapa[String(indice + 1)] = item;
    });
    return mapa;
}

/** Resolve as variáveis AUTOMÁTICAS que o servidor preenche no envio. */
export interface ValoresAutomaticos {
    /** Nome do contato destinatário, quando conhecido. */
    nome_contato?: string | null;
    /** Nome de perfil do WhatsApp, quando conhecido. */
    nome_whatsapp?: string | null;
}

/**
 * Troca os `{{...}}` do texto pelos valores realmente enviados.
 *
 * Existe porque a bolha do template no chat imprimia `bodyJson.text` cru: o
 * `parametersToReplace` viajava ao lado da mensagem e nunca era aplicado, então
 * o operador via `{{nome_do_paciente}}` no lugar de "Maria Claudia".
 *
 * Placeholder sem valor correspondente é deixado como está — inventar um valor
 * mentiria sobre o que a Meta recebeu. A exceção são as variáveis AUTOMÁTICAS
 * (resolvidas pelo servidor): na bolha OTIMISTA, antes do eco, elas ainda não
 * têm valor nenhum, e `automaticos` permite resolvê-las localmente.
 */
export function aplicarParametros(
    text: string | null | undefined,
    params: IMetaParams | null | undefined,
    secao: SecaoParametro,
    automaticos: ValoresAutomaticos = {},
): string {
    const conteudo = text ?? '';
    if (!conteudo) return conteudo;

    const valores = params && !('replace' in params)
        ? valoresPorChave(secao === 'header' ? params.header : params.body)
        : {};

    return conteudo.replace(PLACEHOLDER_GLOBAL, (inteiro, bruto: string, posicao: number) => {
        const chave = (bruto ?? '').trim();

        const informado = valores[chave];
        if (informado !== undefined && informado !== '') return informado;

        const canonica = canonicalVariableName(chave);
        if (!canonica) return inteiro;

        // Automática: o valor real veio do servidor (e então já estaria em `valores`).
        // Chegar aqui é o caso da bolha otimista — resolve com o que a tela sabe.
        const doChamador = automaticos[canonica as keyof ValoresAutomaticos];
        if (doChamador) return String(doChamador);

        if (canonica === 'saudacao') {
            const saudacao = saudacaoLocal();
            return startsSentenceAt(conteudo, posicao) ? capitalizarPrimeira(saudacao) : saudacao;
        }

        return inteiro;
    });
}

/**
 * Mesma faixa horária que o servidor usa em `resolverVariaveisAutomaticas`.
 *
 * Aproximação deliberada: o servidor calcula pelo fuso da CONEXÃO
 * (`Inbox.timeZone`) e aqui só existe o fuso do navegador. Vale apenas para a
 * bolha otimista, que o eco substitui em seguida pelo texto real.
 */
function saudacaoLocal(agora: Date = new Date()): string {
    const hora = agora.getHours();
    if (hora >= 5 && hora <= 11) return 'bom dia';
    if (hora >= 12 && hora <= 17) return 'boa tarde';
    return 'boa noite';
}

/**
 * Exemplos declarados no JSON de um componente, por nome de parâmetro.
 *
 * A Meta guarda os exemplos dentro do próprio componente, em três formatos
 * conforme o tipo do template. Antes esta leitura estava duplicada em
 * `MetaWhatsappTemplateForm` e `ModalExemploApiDisparo`, e nenhuma das duas
 * cópias lia o formato POSICIONAL.
 */
export function exemplosDoComponente(json: Record<string, unknown> | null): Record<string, string> {
    const exemplo = json?.example as Record<string, unknown> | undefined;
    if (!exemplo) return {};

    const mapa: Record<string, string> = {};

    for (const chave of ['body_text_named_params', 'header_text_named_params']) {
        const lista = exemplo[chave];
        if (!Array.isArray(lista)) continue;
        for (const item of lista) {
            const { param_name: nome, example: valor } = (item ?? {}) as { param_name?: string; example?: string };
            if (nome && valor) mapa[nome] = valor;
        }
    }

    // POSICIONAL: `body_text` é `string[][]` (uma linha de exemplo por conjunto) e
    // `header_text` é `string[]`. A posição — 1-based — é a chave no texto.
    const corpoPosicional = exemplo['body_text'];
    if (Array.isArray(corpoPosicional)) {
        const linha = Array.isArray(corpoPosicional[0]) ? corpoPosicional[0] : corpoPosicional;
        (linha as unknown[]).forEach((valor, indice) => {
            if (typeof valor === 'string' && valor) mapa[String(indice + 1)] = valor;
        });
    }

    const cabecalhoPosicional = exemplo['header_text'];
    if (Array.isArray(cabecalhoPosicional)) {
        cabecalhoPosicional.forEach((valor, indice) => {
            if (typeof valor === 'string' && valor) mapa[String(indice + 1)] = valor;
        });
    }

    return mapa;
}

/**
 * Valor de exemplo a mostrar no placeholder do campo — o que a Meta aprovou
 * para aquela variável. Sem exemplo declarado, o nome humanizado.
 */
export function exemploDoCampo(campo: CampoParametro): string {
    return campo.example?.trim()
        || (canonicalVariableName(campo.key) ? templateVariableDef(canonicalVariableName(campo.key)!).example : '')
        || defaultExampleForCustomVariable(campo.key);
}
